import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserContext } from '../App';

const MoonHover = ({ children }) => {
  const { easterEggs, currentUser, setRocketShipFound } = useContext(UserContext);
  const [hoverTime, setHoverTime] = useState(0);
  const [isShaking, setIsShaking] = useState(false);
  const [showRocket, setShowRocket] = useState(false);
  const [rocketPosition, setRocketPosition] = useState({ x: 0, y: 0 });
  const hoverTimerRef = useRef(null);
  const containerRef = useRef(null);
  const hasSavedAchievement = useRef(false);

  useEffect(() => {
    if (hoverTime > 0) {
      // Start shake at 7 seconds
      if (hoverTime >= 7000 && hoverTime < 9000) {
        setIsShaking(true);
      }
      
      // Launch UFO at 9 seconds
      if (hoverTime >= 9000 && !showRocket) {
        setIsShaking(false);
        setShowRocket(true);
        
        // Track achievement when rocket is first triggered
        if (!hasSavedAchievement.current && currentUser && setRocketShipFound) {
          hasSavedAchievement.current = true;
          setRocketShipFound(currentUser.uid);
        }
        
        // Get container position for rocket launch
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setRocketPosition({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          });
        }
        
        // Reset after animation completes
        setTimeout(() => {
          setShowRocket(false);
          setHoverTime(0);
        }, 3500);
      }
    } else {
      setIsShaking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverTime, showRocket]);

  const handleMouseEnter = () => {
    // Only activate if rocket ship easter egg is enabled
    if (easterEggs?.rocketShip !== false) {
      hoverTimerRef.current = setInterval(() => {
        setHoverTime(prev => prev + 100);
      }, 100);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearInterval(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverTime(0);
    setIsShaking(false);
    setShowRocket(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearInterval(hoverTimerRef.current);
      }
    };
  }, []);

  // Shake animation variants
  const shakeVariants = {
    shake: {
      x: [0, -2, 2, -2, 2, -1, 1, 0],
      y: [0, 1, -1, 1, -1, 0.5, -0.5, 0],
      transition: {
        duration: 0.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    normal: {
      x: 0,
      y: 0
    }
  };


  // UFO animation variants
  const ufoVariants = {
    initial: {
      scale: 0,
      opacity: 0,
      y: 0,
      x: 0,
      rotate: 0
    },
    fly: {
      scale: [0, 1, 1, 1, 0],
      opacity: [0, 1, 1, 1, 0],
      y: [0, -50, -60, -50, -1000],
      rotate: [0, 0, -10, 10, 720],
      x: [0, 0, -20, 20, 0],
      transition: {
        duration: 3.5,
        times: [0, 0.15, 0.3, 0.5, 1],
        ease: "easeInOut"
      }
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative w-full h-full"
    >
      <motion.div
        animate={isShaking ? "shake" : "normal"}
        variants={shakeVariants}
        className="w-full h-full"
      >
        {children}
      </motion.div>

      <AnimatePresence>
        {showRocket && (
          <>
            {/* UFO */}
            <motion.div
              initial="initial"
              animate="fly"
              exit={{ opacity: 0 }}
              variants={ufoVariants}
              className="fixed pointer-events-none z-50"
              style={{
                left: rocketPosition.x - 30, // Center the larger icon
                top: rocketPosition.y - 30,
              }}
            >
              <div className="text-6xl filter drop-shadow-lg">🛸</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MoonHover;
